import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import { Warning, Error as ErrorIcon } from "@material-ui/icons";
import { PrimaryText } from "components/Common/PrimaryText";
import { functions } from "utils/firebase";
import { httpsCallable } from "firebase/functions";

const buildHistoricalSnapshots = httpsCallable(
  functions,
  "buildHistoricalSnapshots"
);

const cleanStaleHistoricalEngagement = httpsCallable(
  functions,
  "cleanStaleHistoricalEngagement"
);

const BuildHistoricalDataModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [cleanLoading, setCleanLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleBuildHistoricalData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Start the build process
      await buildHistoricalSnapshots();

      // Close dialog immediately after starting
      onClose();

      // Show success message after closing
      setTimeout(() => {
        onSuccess(
          "Historical data build started! You can track the progress in the floating button."
        );
        // Trigger a global event to notify BuildProgressButton to check progress
        window.dispatchEvent(new CustomEvent("buildProgressStarted"));
      }, 100);
    } catch (err) {
      console.error("Error building historical data:", err);
      setError(err.message || "Failed to start building historical data");
      setLoading(false);
    }
  };

  const handleCleanStaleEngagement = async () => {
    setCleanLoading(true);
    setError(null);

    try {
      const result = await cleanStaleHistoricalEngagement();
      onClose();
      setTimeout(() => {
        onSuccess(
          `Cleaned ${result.data.updated} historical snapshots with stale engagement data.`
        );
      }, 100);
    } catch (err) {
      console.error("Error cleaning stale engagement data:", err);
      setError(err.message || "Failed to clean stale engagement data");
      setCleanLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <PrimaryText fontSize={20} fontWeight={600}>
          Build All Historical Retention Data
        </PrimaryText>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Paper
            style={{
              padding: 16,
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: 4,
            }}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <Warning style={{ color: "#1565c0", marginRight: 8 }} />
              <PrimaryText fontSize={16} fontWeight={600} color="#1565c0">
                Build Historical Data
              </PrimaryText>
            </Box>
            <Typography
              variant="body2"
              style={{ color: "#1565c0", marginBottom: 8 }}
            >
              This action will:
            </Typography>
            <ul
              style={{ margin: "8px 0", paddingLeft: "20px", color: "#1565c0" }}
            >
              <li>Find the oldest user signup date</li>
              <li>Build snapshots from that date to today</li>
              <li>Skip dates that already have data</li>
              <li>Run in the background (no timeout)</li>
              <li>Show progress in a floating button</li>
            </ul>
            <Typography variant="body2" style={{ color: "#1565c0" }}>
              The process will continue even if you close this dialog.
            </Typography>
          </Paper>
        </Box>

        <Box mb={2}>
          <Paper
            style={{
              padding: 16,
              backgroundColor: "#fff8e1",
              border: "1px solid #ffe082",
              borderRadius: 4,
            }}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <Warning style={{ color: "#e65100", marginRight: 8 }} />
              <PrimaryText fontSize={16} fontWeight={600} color="#e65100">
                Clean Stale Engagement Data
              </PrimaryText>
            </Box>
            <Typography
              variant="body2"
              style={{ color: "#e65100", marginBottom: 8 }}
            >
              Run this once after deploying the engagement metrics fix:
            </Typography>
            <ul
              style={{ margin: "8px 0", paddingLeft: "20px", color: "#e65100" }}
            >
              <li>Finds historical snapshots with incorrect lifetime totals</li>
              <li>Nullifies avgMessages, avgSwipes, avgMatches fields</li>
              <li>Completes immediately (no background process)</li>
            </ul>
          </Paper>
        </Box>

        {error && (
          <Box mb={2}>
            <Paper
              style={{
                padding: 16,
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: 4,
              }}
            >
              <Box display="flex" alignItems="center">
                <ErrorIcon style={{ color: "#721c24", marginRight: 8 }} />
                <PrimaryText fontSize={14} color="#721c24">
                  {error}
                </PrimaryText>
              </Box>
            </Paper>
          </Box>
        )}

        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={3}
          >
            <CircularProgress size={24} style={{ marginRight: 12 }} />
            <PrimaryText>Starting build process...</PrimaryText>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading || cleanLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleCleanStaleEngagement}
          color="default"
          variant="outlined"
          disabled={loading || cleanLoading}
        >
          {cleanLoading ? "Cleaning..." : "Clean Stale Engagement"}
        </Button>
        <Button
          onClick={handleBuildHistoricalData}
          color="primary"
          variant="contained"
          disabled={loading || cleanLoading}
        >
          {loading ? "Starting..." : "Start Building"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuildHistoricalDataModal;
